import { useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import dayjs, { Dayjs } from 'dayjs'
import { GridApi } from 'ag-grid-enterprise'
import { filtersSelector } from '../../redux/context/filters/selectors'
import { NumberFormatConfig } from '../utils/numberScale'

export interface GridContext {
  forecastCursor?: Dayjs
  isEditMode?: boolean
  showCalculated?: boolean
  showInput?: boolean
  showPercentage?: boolean
  /** User's toolbar-level scale/decimals choice for value cell formatting. */
  numberFormat?: NumberFormatConfig
}

interface UseGridContextOptions {
  /**
   * AG Grid API instance - used to sync context to grid
   */
  gridApi?: GridApi<any>
  /**
   * Whether the grid is in edit mode
   */
  isEditMode?: boolean
  /**
   * Whether to show calculated budget values
   */
  showCalculated?: boolean
  /**
   * Whether to show input budget values
   */
  showInput?: boolean
  showPercentage?: boolean
  /**
   * User's toolbar-level scale/decimals choice. Must be a memoized object —
   * a fresh literal every render defeats this hook's own memoization and
   * triggers a grid refresh on every render.
   */
  numberFormat?: NumberFormatConfig
}

/**
 * Shared hook for creating AG Grid context and syncing it to grid API
 * This context is passed to AG Grid and can be accessed in cell renderers,
 * value getters, value setters, and other grid callbacks via params.context
 *
 * @param options - Configuration options
 * @param options.gridApi - AG Grid API instance (optional, used for auto-sync)
 * @param options.isEditMode - Whether the grid is in edit mode (controls cell editability)
 * @param options.showCalculated - Whether to show calculated budget values
 * @param options.showInput - Whether to show input budget values
 * @param options.showPercentage - Whether to show input percentage on cell
 * @returns Grid context object
 *
 * @example
 * ```tsx
 * const [gridApi, setGridApi] = useState<GridApi>()
 * const [isEditMode, setIsEditMode] = useState(false)
 * const gridContext = useGridContext({ gridApi, isEditMode })
 *
 * <CustomAgGrid
 *   context={gridContext}
 *   onGridReady={e => setGridApi(e.api)}
 *   // ...other props
 * />
 * ```
 */
export const useGridContext = (options: UseGridContextOptions = {}): GridContext => {
  const { gridApi, isEditMode = false, showCalculated, showInput, showPercentage, numberFormat } = options
  const { budgetingScenario } = useSelector(filtersSelector)

  const forecastCursor = useMemo(
    () => (budgetingScenario?.forecastCursor ? dayjs(budgetingScenario.forecastCursor) : undefined),
    [budgetingScenario?.forecastCursor]
  )

  const gridContext = useMemo(
    () => ({
      forecastCursor,
      isEditMode,
      showCalculated,
      showInput,
      showPercentage,
      numberFormat,
      statemenType: 'incomestatement'
    }),
    [forecastCursor, isEditMode, showCalculated, showInput, showPercentage, numberFormat]
  )

  // Sync context to GridApi when it changes
  useEffect(() => {
    if (!gridApi) return

    const currentContext = gridApi.getGridOption('context') || {}
    gridApi.setGridOption('context', {
      ...currentContext,
      ...gridContext
    })

    // Update cell editability and display without flashing
    gridApi.refreshCells({
      force: true, // Only refresh cells that need updating
      suppressFlash: true // Prevent flash animation
    })

    // Refresh headers to update disabled state based on context
    gridApi.refreshHeader()
  }, [gridApi, gridContext])

  return gridContext
}
